import apiClient from '../client';

// Proof that the caller owns the order. A logged-in customer is recognised by the JWT apiClient already sends; a guest
// sends the checkout token issued when they verified their e-mail (the backend refuses payment start / COD confirmation
// without one — order numbers alone are guessable).
const CHECKOUT_SESSION_KEY = 'skt_checkout_session';
export const checkoutHeaders = () => {
  let token = null;
  try { token = sessionStorage.getItem('checkout_token'); } catch { /* storage unavailable */ }
  if (!token) {
    try {
      const raw = localStorage.getItem(CHECKOUT_SESSION_KEY) || sessionStorage.getItem(CHECKOUT_SESSION_KEY);
      const session = raw ? JSON.parse(raw) : null;
      if (session && Date.now() < session.expiresAt) token = session.token;
    } catch { /* ignore */ }
  }
  return token ? { 'X-Checkout-Token': token } : {};
};


/**
 * GET /payment-options
 * Returns enabled payment gateways for the checkout UI.
 * Public endpoint — no auth required.
 * Example: [{providerName: "RAZORPAY", displayName: "Razorpay", keyId: "rzp_live_xxx"}, {providerName: "COD", ...}]
 */
export const getPaymentOptions = async (marketCode) => {
  const params = marketCode ? { marketCode } : {};
  const { data } = await apiClient.get('/payment-options', { params });
  return data;
};

/**
 * POST /payments/initiate
 * Creates a Razorpay order on the backend and returns razorpayOrderId + key.
 */
export const initiatePayment = async (orderNumber, amount, providerName = null) => {
  const { data } = await apiClient.post('/payments/initiate', {
    orderNumber,
    amount,
    receiptId: orderNumber,
    ...(providerName ? { providerName } : {}),
  }, { headers: checkoutHeaders() });
  return data; // { razorpayOrderId, razorpayKeyId, amount, currency, orderNumber, customerEmail, customerName, customerPhone }
};

/**
 * POST /payments/verify
 * Verifies the Razorpay HMAC signature and marks the order as PAID.
 */
export const verifyPayment = async ({ razorpayOrderId, razorpayPaymentId, razorpaySignature, orderNumber }) => {
  const { data } = await apiClient.post('/payments/verify', {
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
    orderNumber,
  });
  return data; // OrderDto
};

/**
 * POST /payments/cod/confirm?orderNumber=
 * Confirms a Cash on Delivery order.
 */
export const confirmCOD = async (orderNumber) => {
  const { data } = await apiClient.post('/payments/cod/confirm', null, {
    params: { orderNumber },
    headers: checkoutHeaders(),
  });
  return data; // OrderDto
};

/**
 * Dynamically loads the Razorpay checkout script.
 * Returns a promise that resolves when the script is ready.
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(script);
  });
};

/**
 * Opens the Razorpay payment modal.
 * @param {object} paymentInit  - response from initiatePayment
 * @param {object} order        - the created OrderDto
 * @param {function} onSuccess  - called with { razorpayOrderId, razorpayPaymentId, razorpaySignature }
 * @param {function} onDismiss  - called when user closes the modal without paying
 */
export const openRazorpayModal = (paymentInit, order, onSuccess, onDismiss) => {
  const options = {
    key: paymentInit.razorpayKeyId,
    amount: paymentInit.amount,               // already in paise from backend
    currency: paymentInit.currency || 'INR',
    name: 'Skinn Thrive',
    description: `Order #${order.orderNumber}`,
    order_id: paymentInit.razorpayOrderId,
    prefill: {
      name: paymentInit.customerName || order.shippingAddress?.name || '',
      email: paymentInit.customerEmail || order.email || '',
      contact: paymentInit.customerPhone || order.phone || '',
    },
    theme: { color: '#5B21B6' },
    modal: {
      ondismiss: onDismiss,
    },
    handler: (response) => {
      onSuccess({
        razorpayOrderId: response.razorpay_order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpaySignature: response.razorpay_signature,
      });
    },
  };
  const rzp = new window.Razorpay(options);
  rzp.open();
};
